import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ChevronDown, MessageSquare, MapPin } from 'lucide-react';
import { UserStatus, STATUS_OPTIONS, getStatusOption } from '@/types/presence';
import { useUserPresence } from '@/hooks/useUserPresence';
import { SetLocationDialog } from './SetLocationDialog';
import { toast } from '@/hooks/use-toast';

interface StatusSelectorProps {
  className?: string;
}

export const StatusSelector: React.FC<StatusSelectorProps> = ({ className }) => {
  const { myPresence, updateMyStatus } = useUserPresence();
  const [statusMessage, setStatusMessage] = useState(myPresence?.status_message || '');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);

  const currentStatus = myPresence?.status || 'offline';
  const currentStatusOption = getStatusOption(currentStatus);

  const handleStatusChange = async (status: UserStatus, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await updateMyStatus(status, statusMessage || undefined);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de changer le statut. Veuillez réessayer.',
        variant: 'destructive'
      });
    }
  };

  const handleStatusWithMessage = async () => {
    try {
      await updateMyStatus(currentStatus, statusMessage || undefined);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to update status message:', error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 px-2 gap-2 ${className}`}
        >
          <span className="text-lg">{currentStatusOption.icon}</span>
          <span className={`text-sm font-medium ${currentStatusOption.color}`}>
            {currentStatusOption.label}
          </span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {STATUS_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
            className="flex items-center gap-3 py-2 cursor-pointer"
          >
            <span className="text-lg">{option.icon}</span>
            <div className="flex-1">
              <div className={`font-medium ${option.color}`}>
                {option.label}
              </div>
              <div className="text-xs text-muted-foreground">
                {option.description}
              </div>
            </div>
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setIsLocationDialogOpen(true);
          }}
          className="flex items-center gap-3 py-2 cursor-pointer"
        >
          <MapPin className="h-4 w-4 text-orange-600" />
          <div className="flex-1">
            <div className="font-medium text-orange-600">
              🧳 Je suis disponible ici
            </div>
            <div className="text-xs text-muted-foreground">
              Annoncer votre présence temporaire
            </div>
          </div>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              className="flex items-center gap-3 py-2 cursor-pointer"
            >
              <MessageSquare className="h-4 w-4" />
              <span>Personnaliser le message</span>
            </DropdownMenuItem>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Message de statut personnalisé</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-lg">{currentStatusOption.icon}</span>
                <span className={`font-medium ${currentStatusOption.color}`}>
                  {currentStatusOption.label}
                </span>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status-message">Message (optionnel)</Label>
                <Input
                  id="status-message"
                  value={statusMessage}
                  onChange={(e) => setStatusMessage(e.target.value)}
                  placeholder="Ex: En réunion jusqu'à 15h, Disponible pour projets React..."
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground">
                  {statusMessage.length}/100 caractères
                </p>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button onClick={handleStatusWithMessage}>
                  Confirmer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        <DropdownMenuSeparator className={!myPresence?.status_message ? 'hidden' : ''} />
        <div className={`px-2 py-1 ${!myPresence?.status_message ? 'hidden' : ''}`}>
          <div className="text-xs text-muted-foreground">Message actuel:</div>
          <div className="text-sm font-medium truncate">
            {myPresence?.status_message || ''}
          </div>
        </div>
      </DropdownMenuContent>
      
      <SetLocationDialog 
        open={isLocationDialogOpen}
        onOpenChange={setIsLocationDialogOpen}
      />
    </DropdownMenu>
  );
};